import { flip, offset, shift, useFloating } from "@floating-ui/react-dom";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import { cn } from "@btnet/utilities-core";

import { BdsText } from "@buildertrend/components";

import { type ITemplateVariableSuggestionItem } from "commonComponents/btWrappers/editor/core/extensions/custom/templateVariable/TemplateVariableExtension";

import "./TemplateVariableSelectOverlay.less";

export interface ITemplateVariableSelectOverlayProps {
    items: ITemplateVariableSuggestionItem[];
    onSelect: (item: ITemplateVariableSuggestionItem) => void;
    /** Bounding rect of the `{` trigger in the editor, used to anchor the dropdown. */
    getReferenceRect: () => DOMRect | null;
}

/** Imperative handle the suggestion plugin calls so keyboard nav is driven by the editor's keydown. */
export interface ITemplateVariableSelectOverlayRef {
    onKeyDown: (event: KeyboardEvent) => boolean;
}

interface ITemplateVariableGroup {
    sourceId: string;
    sourceLabel: string;
    items: ITemplateVariableSuggestionItem[];
}

/** DOM id for an option, so the listbox can point `aria-activedescendant` at the active one. */
function getOptionId(item: ITemplateVariableSuggestionItem): string {
    return `templateVar-${item.namespace}-${item.key}`;
}

/** Groups items by source namespace, preserving first-seen order and per-group item order. */
function groupBySource(items: ITemplateVariableSuggestionItem[]): ITemplateVariableGroup[] {
    const groups: ITemplateVariableGroup[] = [];
    const byNamespace = new Map<string, ITemplateVariableGroup>();
    for (const item of items) {
        let group = byNamespace.get(item.namespace);
        if (!group) {
            group = { sourceId: item.namespace, sourceLabel: item.sourceLabel, items: [] };
            byNamespace.set(item.namespace, group);
            groups.push(group);
        }
        group.items.push(item);
    }
    return groups;
}

export const TemplateVariableSelectOverlay = forwardRef<
    ITemplateVariableSelectOverlayRef,
    ITemplateVariableSelectOverlayProps
>(function TemplateVariableSelectOverlay({ items, onSelect, getReferenceRect }, ref) {
    const [selectedIndex, setSelectedIndex] = useState(0);
    // Keep the latest index for the imperative handler without re-subscribing.
    const selectedIndexRef = useRef(0);
    useEffect(() => {
        selectedIndexRef.current = selectedIndex;
    }, [selectedIndex]);

    // Show source headers only when more than one source contributes items; a single source reads as
    // a plain flat list. Keyboard nav always walks the flat `items` array regardless of grouping.
    const groups = useMemo(() => groupBySource(items), [items]);
    const showGroupHeaders = groups.length > 1;

    const { x, y, strategy, refs } = useFloating({
        placement: "bottom-start",
        middleware: [flip(), shift({ padding: 5 }), offset(5)],
    });

    // Keep the highlighted option visible when keyboard nav moves it past the overlay's scroll edge.
    useEffect(() => {
        const active = refs.floating.current?.querySelector('[aria-selected="true"]');
        active?.scrollIntoView({ block: "nearest" });
    }, [selectedIndex, refs.floating]);

    useLayoutEffect(() => {
        // Reset the highlight whenever the filtered list changes so it never points past the end.
        setSelectedIndex(0);
    }, [items]);

    useLayoutEffect(() => {
        refs.setReference({ getBoundingClientRect: () => getReferenceRect() ?? new DOMRect() });
    }, [refs, getReferenceRect]);

    useImperativeHandle(
        ref,
        () => ({
            onKeyDown: (event) => {
                if (items.length === 0) {
                    return false;
                }
                if (event.key === "ArrowUp") {
                    setSelectedIndex((prev) => (prev + items.length - 1) % items.length);
                    return true;
                }
                if (event.key === "ArrowDown") {
                    setSelectedIndex((prev) => (prev + 1) % items.length);
                    return true;
                }
                if (event.key === "Enter" || event.key === "Tab") {
                    const item = items[selectedIndexRef.current];
                    if (item) {
                        onSelect(item);
                    }
                    return true;
                }
                return false;
            },
        }),
        [items, onSelect],
    );

    const renderOption = (item: ITemplateVariableSuggestionItem, flatIndex: number) => (
        <li
            key={`${item.namespace}:${item.key}`}
            id={getOptionId(item)}
            data-testid={`templateVariableOption-${item.namespace}-${item.key}`}
            className={cn(
                "flex cursor-pointer flex-col rounded-sm px-xs py-xss hover:bg-surface-default-hover",
                flatIndex === selectedIndex && "bg-surface-selected",
            )}
            role="option"
            aria-selected={flatIndex === selectedIndex}
            onMouseEnter={() => setSelectedIndex(flatIndex)}
            onClick={() => onSelect(item)}
        >
            <div className="flex items-center justify-between gap-sm">
                <BdsText size="normal-sm" text={item.label} />
                {item.value !== undefined && (
                    <BdsText
                        size="normal-sm"
                        className="font-semibold text-fg-tertiary"
                        text={item.value}
                    />
                )}
            </div>
            {/* Description spans full width beneath the label/value row so it wraps rather than
                widening the popup. */}
            {item.description && (
                <BdsText size="normal-sm" className="text-fg-secondary" text={item.description} />
            )}
        </li>
    );

    // Walking index shared across groups so keyboard selection maps to the flat `items` order.
    let flatIndex = -1;

    return (
        <div
            ref={(el) => refs.setFloating(el)}
            className="TemplateVariableSelectOverlay z-4 overflow-y-auto rounded-sm bg-surface-default shadow-floating"
            style={{ position: strategy, top: y ?? undefined, left: x ?? undefined }}
            role="listbox"
            aria-label="Template variable suggestions"
            // Focus stays in the editor, which drives keyboard nav; the listbox only needs a tabindex
            // to satisfy the aria-activedescendant contract, not to actually receive focus.
            tabIndex={-1}
            aria-activedescendant={
                items[selectedIndex] ? getOptionId(items[selectedIndex]) : undefined
            }
        >
            {items.length === 0 ? (
                <ul className="m-0 list-none p-xss">
                    <li className="px-xs py-xss">
                        <BdsText size="normal-sm" text="No variables found" />
                    </li>
                </ul>
            ) : (
                groups.map((group) => (
                    <ul
                        key={group.sourceId}
                        className="m-0 list-none p-xss"
                        role="group"
                        aria-label={group.sourceLabel}
                    >
                        {showGroupHeaders && (
                            <li className="px-xs pb-xss pt-xs">
                                <BdsText
                                    size="normal-sm"
                                    className="font-semibold text-fg-secondary"
                                    text={group.sourceLabel}
                                />
                            </li>
                        )}
                        {group.items.map((item) => {
                            flatIndex += 1;
                            return renderOption(item, flatIndex);
                        })}
                    </ul>
                ))
            )}
        </div>
    );
});
